<template lang="pug">
.v-select-wrapper
  label.v-select__label(:class="{ 'v-select__label--invalid': invalid }") {{ label | translate }}
  .v-select(v-click-outside="hideItems"
  :class="{ 'v-select--invalid': invalid, 'v-select--has-value': hasValue && !toggled, 'v-select--toggled': toggled }")
    .v-select__field(
      :class="{ 'v-select__field--disabled': disabled, 'v-select__field--invalid': invalid }"
      @click="toggle")
      input.v-select__field-input(
        v-focus
        v-if="toggled"
        v-model="search"
        type="text"
        :placeholder="searchPlaceholder"
        @click.stop)
      .v-select__title(
        v-else
        :title="hasValue ? selectTitle : ''"
        :class="{ 'v-select__title--placeholder': !hasValue }")
        | {{ displayTitle }}
      .v-select__bar
      .v-select__icon
        v-chevron(:value="toggled")
    transition(name="fade")
      .v-select__items(
        v-if="toggled"
        :class="{ 'v-select__items--fix-height': filteredOptions.length > 4 }")
        vue-scroll
          .item(
            v-for="item in filteredOptions"
            :key="item.id"
            :class="{ 'item--selected': isSelected(item) }"
            @click="selectItem(item)")
            | {{ item[itemName] | translate }}
          .empty(v-if="filteredOptions.length === 0")
            | {{ 'base.no_options' | translate }}
  .v-select__hint-wrapper
    .v-select__hint(v-if="hint") {{ hint }}
</template>

<script>
import VChevron from '../v-chevron/VChevron'

export default {
  name: 'VSelect',

  components: {
    VChevron
  },

  props: {
    label: String,
    value: [String, Number, Boolean, Object],
    options: {
      type: Array,
      default: () => ([])
    },
    searchPlaceholder: {
      type: String,
      default: null
    },
    itemValue: {
      type: String,
      default: 'id'
    },
    itemName: {
      type: String,
      default: 'name'
    },
    hint: {
      type: String,
      default: null
    },
    invalid: {
      type: Boolean,
      default: false
    },
    returnObject: Boolean,
    disabled: Boolean,
    apiSearch: Boolean,
    labelActive: {
      type: Boolean,
      default: false
    },
    selectPlaceholder: {
      type: String,
      default: function () {
        return this.$t('base.select')
      }
    }
  },

  data: () => ({
    search: '',
    toggled: false,
    hasBlurred: false
  }),

  filters: {
    shortText (text) {
      return text && text.length > 28
        ? (text.substring(0, 28) + '...')
        : text
    }
  },

  computed: {
    selectTitle () {
      if (this.value !== null && this.value !== undefined && this.value !== '') {
        if (this.returnObject) {
          return this.value[this.itemName]
        } else {
          const selected = this.options
            .find(item => item[this.itemValue] === this.value)
          if (selected) {
            return selected[this.itemName]
          }
        }
      }
      return this.label
    },

    filteredOptions () {
      return this.apiSearch
        ? this.options
        : this.options
          .filter(item => item[this.itemName]
            .startsWith('constants')
            ? this.$t(item[this.itemName])
              .toLowerCase()
              .includes(this.search.toLowerCase())
            : item[this.itemName]
              .toLowerCase()
              .includes(this.search.toLowerCase()))
    },

    hasValue () {
      return this.value !== null && this.value !== undefined && this.value !== ''
    },

    displayTitle () {
      if (!this.hasValue) return this.selectPlaceholder
      const text = this.selectTitle
      if (!text) return text
      const translated = this.$t(text) ? this.$t(text) : text
      return translated.length > 28 ? translated.substring(0, 28) + '...' : translated
    }
  },

  methods: {
    isSelected (item) {
      if (this.returnObject) {
        return this.value && this.value[this.itemValue] === item[this.itemValue]
      }
      return this.value === item[this.itemValue]
    },

    selectItem (item) {
      this.toggled = !this.toggled
      this.$emit('input', this.returnObject ? item : item[this.itemValue])
      this.hasBlurred = true
    },

    toggle () {
      if (!this.disabled) {
        this.toggled = !this.toggled
      }
    },

    hideItems () {
      if (this.toggled) {
        this.toggled = false
        this.hasBlurred = true
        this.$emit('blur')
      }
    }
  },

  watch: {
    search (value) {
      this.$emit('search', value)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/sass/abstracts/_variables.scss';

.v-select-wrapper {
  padding-top: 0;
}

.v-select__label {
  display: block;
  color: $color-gray;
  font-size: 1.3rem;
  margin-bottom: .5rem;
  pointer-events: none;
  line-height: 1.3rem;

  &--invalid {
    color: $color-red !important;
  }
}

.v-select {
  position: relative;
  margin-bottom: 0.2rem;

  &__field {
    position: relative;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1.5rem;
    width: 100%;
    height: 4rem;
    padding: 1rem;
    border-radius: 1rem;
    background: $color-hover;
    border: .2rem solid transparent;
    transition: background-color 0.2s ease, border-color 0.2s ease;

    &--disabled {
      opacity: .5;
    }

    &--invalid {
      background: #FFE3E3 !important;
    }

    &-input {
      width: 100%;
      border: none;
      font-size: 1.4rem;
      background: transparent;

      &:focus {
        outline: none;
      }
    }
  }

  &__title {
    flex: 1;
    margin-right: 1.8rem;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    max-width: 24rem;
    font-size: 1.5rem;
    user-select: none;
    pointer-events: none;

    &--placeholder {
      color: $color-gray;
      font-weight: 400;
    }
  }

  &__bar {
    &:before,
    &:after {
      content: "";
      height: .2rem;
      width: 0;
      bottom: 0;
      position: absolute;
      background: $color-primary;
      transition: 0.2s ease all;
    }

    &:before {
      left: 50%;
    }

    &:after {
      right: 50%;
    }
  }

  &__icon {
    transition: transform .3s ease;
    position: relative;
    top: 0.1rem;
    flex-shrink: 0;
  }

  &__items {
    position: absolute;
    z-index: 1000;
    width: 100%;
    min-height: 4rem;
    background: $color-white;
    padding-bottom: 0;
    padding-top: 0.5rem;
    border-radius: 1rem;
    box-shadow: 2px 2px 5px 0px rgba(0, 0, 0, .2);
    display: flex;
    flex-direction: column;
    gap: 5px;

    &--fix-height {
      height: 21rem;
    }

    .item {
      cursor: pointer;
      user-select: none;
      position: relative;
      padding: 1rem;
      width: calc(100% - 1rem);
      margin: 0 auto;
      font-size: 1.5rem;
      transition: 0.2s ease all;
      border-radius: 1rem;
      margin-bottom: 5px;
      &:last-child {
        margin-bottom: 0;
      }

      &:hover {
        background: #eee;
      }

      &--selected {
        background-color: #DDF2E3;
      }
    }

    .empty {
      font-size: 1.5rem;
      padding: .5rem 1rem;
    }
  }

  &__hint-wrapper {
    min-height: 1.5rem;
  }

  &__hint {
    color: $color-red;
    font-size: 1.2rem;
    font-weight: 300;
  }

  &--invalid {
    .v-select__field {
      background: #FFE3E3 !important;
    }

    .v-select__bar:before,
    .v-select__bar:after {
      background-color: transparent !important;
    }
  }

  &--has-value {
    .v-select__field {
      background-color: #F4F4F4;
    }
  }

  &--toggled {
    .v-select__field {
      background-color: #DDF2E3;
    }
  }
}
</style>